/* eslint-disable @next/next/no-html-link-for-pages */
/* eslint-disable @next/next/no-img-element */
import { Breadcrumb } from 'antd'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { Fade as Hamburger } from 'hamburger-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { Fragment, useMemo } from 'react'
import { useQueries, useQuery } from 'react-query'
import { SNOW_DOG_MENU_NODES } from '~src/api'
import { GET_CATEGORIES_BY_URL_KEY } from '~src/api/categories'
import { graphQLClientGET } from '~src/api/graphql'
import { SearchBox } from '~src/components'
import { CartCount } from '~src/components/common/CartCount'
import LanguageDropdown from '~src/components/global/Language'
import UserInformation from '~src/components/global/UserInformation'
import { categoryChildrenQuery, useCategoryChildren } from '~src/hook/useCategoryChildren'
import { sortByPosition } from '~src/util'
import { Menu } from './menu'

/** Danh mục con của Hãng xe → mục menu, link tới /catalog-category/<url_key> */
const toCatalogMenuItems = (items: TChildrenCategories[]) =>
  sortByPosition(items).map((item) => ({
    ...item,
    title: item.name,
    content: item.url_key,
    url_key: `catalog-category/${item.url_key}`,
    category_url_key: item.url_key,
  }))

/** Cột đời xe: chỉ gọi API khi rê chuột vào dòng xe (tải trước ~57 dòng sẽ quá nhiều request) */
const CategoryChildrenMenu: React.FC<{ urlKey: string; initData: any[]; className: string }> = ({
  urlKey,
  initData,
  className,
}) => {
  const { data } = useCategoryChildren(urlKey)
  const items = useMemo(() => toCatalogMenuItems(data ?? []), [data])
  if (!items.length) return null
  return (
    <div className={className}>
      <Menu data={items} initData={initData} />
    </div>
  )
}

type TProps = {
  isBreadcrumb: boolean
  isScroll?: boolean
  headerRef: any
  hover: boolean
  handleHover: (bol: boolean) => void
  dataInfoCustomer: any
  isLoadingDataInfoCustomer: boolean
}

const Header: React.FC<TProps> = ({
  isBreadcrumb = false,
  isScroll,
  headerRef,
  hover,
  handleHover,
  dataInfoCustomer,
  isLoadingDataInfoCustomer,
}) => {
  const router = useRouter()
  const breadcrumbs: any = [{ name: 'Trang chủ', key: null }]

  const defaultData = [
    'Hãng xe',
    'Thương hiệu',
    'Nhóm phụ tùng',
    'Khuyến mãi',
    'Tra vận đơn',
    'Nhật ký',
  ]

  const { data: dataHeaderMenu } = useQuery<any>({
    queryKey: ['getHeaderMenu'],
    queryFn: async () => {
      const data = (await graphQLClientGET.request(SNOW_DOG_MENU_NODES, {
        identifier: 'mainmenu',
      })) as { snowdogMenuNodes: { items: TMenuHeader[] } }
      return data
    },
    cacheTime: Infinity,
  })

  // Lấy các item có url_key bắt đầu bằng /brand/ từ dataHeaderMenu
  const brandItems = useMemo(() => {
    return (
      dataHeaderMenu?.snowdogMenuNodes?.items?.filter(
        (item: any) => item?.url_key?.startsWith('brand/') || item?.url_key?.includes('brand/')
      ) ?? []
    )
  }, [dataHeaderMenu])

  // API lấy danh mục con cho từng thương hiệu (brand)
  const brandQueries = useQueries(
    brandItems
      .filter((item: any) => item?.classes !== 'brand') //lấy ra list danh mục
      .map((item: any) => {
        const urlKey = item?.url_key?.replace('brand/', '') || ''
        return {
          queryKey: ['getCategoriesByBrand', urlKey],
          queryFn: async () => {
            const res = (await graphQLClientGET.request(GET_CATEGORIES_BY_URL_KEY, {
              eq: urlKey,
            })) as TCategories
            // Magento không trả children theo position nên sắp ngay khi lưu cache
            return sortByPosition(res.categories?.items?.[0]?.children ?? [])
          },
          enabled: !!urlKey,
        }
      })
  )

  // Map url_key -> danh mục con để truyền xuống Menu
  const categoriesByBrandMap = useMemo(() => {
    const map: Record<string, any[]> = {}
    brandItems
      .filter((item: any) => item?.classes !== 'brand') //lấy ra list danh mục
      .forEach((item: any, i: number) => {
        const key = item?.url_key
        if (key && brandQueries[i]?.data) {
          map[key] = (brandQueries[i].data as any[])?.map((item: any) => ({
            ...item,
            title: item.name,
            content: item.url_key,
            url_key: item.url_path,
          }))
        }
        const keyNorm = key?.replace(/^\/+/, '')
        if (keyNorm && keyNorm !== key && brandQueries[i]?.data) {
          map[keyNorm] = brandQueries[i].data as any[]
        }
      })
    return map
  }, [brandItems, brandQueries])

  // Các hãng xe trong menu (node cấp 1 trỏ tới catalog-category): dòng xe lấy từ categories
  const companyItems = useMemo(() => {
    return (
      dataHeaderMenu?.snowdogMenuNodes?.items?.filter(
        (item: any) => item?.level === 1 && item?.url_key?.startsWith('catalog-category/')
      ) ?? []
    )
  }, [dataHeaderMenu])

  // Key trùng useCategoryChildren nên dùng chung cache với Tìm kiếm nhanh và trang catalog
  const companyQueries = useQueries(
    companyItems.map((item: any) =>
      categoryChildrenQuery(item.url_key.replace('catalog-category/', ''))
    )
  )

  // Map url_key hãng -> dòng xe; chưa có dữ liệu thì Menu dùng lại node snowdog
  const categoriesByCompanyMap = useMemo(() => {
    const map: Record<string, any[]> = {}
    companyItems.forEach((item: any, i: number) => {
      const types = companyQueries[i]?.data as TChildrenCategories[] | undefined
      if (types?.length) map[item.url_key] = toCatalogMenuItems(types)
    })
    return map
  }, [companyItems, companyQueries])

  return (
    <header ref={headerRef} className={`app-header ${isScroll ? 'sticky-header' : ''}`}>
      <div className="containers">
        <div className="content-header">
          <div className="logo">
            <Link href="/">
              <a>
                <img src="/image/logo/logo-header-2.png" alt="" />
              </a>
            </Link>
          </div>
          <div className="visible-menu">
            <div className="flex items-center justify-start gap-[0.3rem] md:gap-5 max-h-[70px] max-xc:ml-[-13px]">
              <button
                onClick={() => {
                  handleHover(!hover)
                }}
              >
                <Hamburger toggled={hover} direction="right" size={22} />
              </button>
              <Link href="/" className="block m-8">
                <img
                  src="/image/logo/logo-header-2.png"
                  alt=""
                  className="w-full max-h-[40px] max-lg:max-w-[10.2rem]"
                />
              </Link>
            </div>
          </div>

          {/* <nav className="menu">
            <ul className="whitespace-nowrap menu-wrapper">
              {!!dataMenuNodes ? (
                dataMenuNodes?.map((itemParent: any, index: number) => {
                  const pathname = router?.pathname === '/' ? undefined : router?.pathname
                  const isActive = itemParent?.url_key?.includes(pathname?.split('/')?.[1])
                  const isActiveChild = itemParent?.childrens?.find(
                    (child: any) => child?.title === pathname
                  )
                  return (
                    <li className="relative group" key={index}>
                      <Link
                        href={
                          itemParent?.url_key?.startsWith('/')
                            ? itemParent?.url_key
                            : '/' + itemParent?.url_key
                        }
                      >
                        <a className={clsx((isActive || isActiveChild) && 'menuActive')}>
                          <img
                            src={'/image/icon-hover-top.png'}
                            alt=""
                            className="mx-auto h-[1.3rem] mb-[0.4rem] max-xc:hidden"
                          />
                          <p>{itemParent?.name}</p>
                        </a>
                      </Link>
                      <div className="absolute hidden group-hover:block bg-white z-[1000] shadow-md top-[100%] left-0 header-menu-item">
                        <Menu data={itemParent?.children} />
                      </div>
                    </li>
                  )
                })
              ) : (
                <>
                  {defaultData.map((item, index: number) => {
                    return (
                      <li key={index}>
                        <Link href={'/'}>
                          <a>
                            <span> {item}</span>
                          </a>
                        </Link>
                      </li>
                    )
                  })}
                </>
              )}
            </ul>
          </nav> */}
          <nav className="menu">
            <ul className="whitespace-nowrap menu-wrapper">
              {!!dataHeaderMenu?.snowdogMenuNodes?.items ? (
                dataHeaderMenu?.snowdogMenuNodes?.items.map((itemParent: any, index: number) => {
                  // console.log('🚀 ~ Header ~ itemParent:', itemParent?.brand)
                  const pathname = router?.pathname === '/' ? undefined : router?.pathname
                  const isActive = itemParent?.url_key?.includes(pathname?.split('/')?.[1])
                  const isActiveChild = itemParent?.childrens?.find(
                    (child: any) => child?.title === pathname
                  )

                  const initData = dataHeaderMenu?.snowdogMenuNodes?.items as any
                  const data = [...initData]?.filter(
                    (val: any) => val?.parent_id == itemParent?.node_id
                  )
                  // itemParent.classes == 'brand'
                  //   ? itemParent.brand
                  //   : [...initData]?.filter((val: any) => val?.parent_id == itemParent?.node_id)

                  if (itemParent?.level !== 0) return null
                  const className = 'absolute hidden group-hover:block bg-white z-[1000]  shadow-md'
                  return (
                    <li className="relative group" key={index}>
                      <Link
                        href={
                          itemParent?.content?.startsWith('/')
                            ? itemParent?.content
                            : '/' + itemParent?.content
                        }
                      >
                        <a className={clsx((isActive || isActiveChild) && 'menuActive')}>
                          <img
                            src={'/image/icon-hover-top.png'}
                            alt=""
                            className="mx-auto h-[1.3rem] mb-[0.4rem] max-xc:hidden"
                          />
                          <p>{itemParent?.title}</p>
                        </a>
                      </Link>
                      <div className={clsx(className, 'top-[100%] left-0 header-menu-item')}>
                        <Menu
                          data={data}
                          initData={initData}
                          renderChildren={({ item }) => {
                            return (
                              <div
                                className={clsx(
                                  className,
                                  'min-h-full left-full top-0 no-scrollbar header-menu-child'
                                )}
                              >
                                <Menu
                                  initData={initData}
                                  data={
                                    categoriesByBrandMap[item?.url_key] ||
                                    categoriesByCompanyMap[item?.url_key] ||
                                    item?.children ||
                                    [...initData]?.filter((val) => val?.parent_id == item?.node_id)
                                  }
                                  renderChildren={({ item }) =>
                                    item?.category_url_key ? (
                                      <CategoryChildrenMenu
                                        urlKey={item.category_url_key}
                                        initData={initData}
                                        className={clsx(
                                          className,
                                          'min-h-full left-full top-0 overflow-auto header-menu-child'
                                        )}
                                      />
                                    ) : (
                                      <div
                                        className={clsx(
                                          className,
                                          'min-h-full left-full top-0 overflow-auto header-menu-child'
                                        )}
                                      >
                                        <Menu
                                          initData={initData}
                                          data={
                                            item?.children ||
                                            [...initData]?.filter(
                                              (val) => val?.parent_id == item?.node_id
                                            )
                                          }
                                        />
                                      </div>
                                    )
                                  }
                                />
                              </div>
                            )
                          }}
                        />
                      </div>
                    </li>
                  )
                })
              ) : (
                <>
                  {defaultData.map((item, index: number) => {
                    return (
                      <li key={index}>
                        <Link href={'/'}>
                          <a>
                            <span> {item}</span>
                          </a>
                        </Link>
                      </li>
                    )
                  })}
                </>
              )}
            </ul>
          </nav>

          <div className="sticky">
            <SearchBox router={router} />
            <div className={router.pathname === '/shopping-cart' ? 'hidden' : ''}>
              <CartCount />
            </div>
            <UserInformation
              dataInfoCustomer={dataInfoCustomer}
              isLoadingDataInfoCustomer={isLoadingDataInfoCustomer}
            />

            <LanguageDropdown />
          </div>
        </div>
      </div>

      {isBreadcrumb ? (
        <div className="breadcrumb font-bold m-0">
          {breadcrumbs.length > 0 && (
            <Breadcrumb>
              {breadcrumbs?.map((item: any, index: number) => (
                <Breadcrumb.Item key={index}>
                  {item.key ? (
                    <Link href={item.key} className="font-medium">
                      {item.name}
                    </Link>
                  ) : (
                    item.name
                  )}
                </Breadcrumb.Item>
              ))}
              {router.query && router.query?.Name && router.query?.Name?.length > 0 && (
                <Breadcrumb.Item>{router.query.Name}</Breadcrumb.Item>
              )}
            </Breadcrumb>
          )}
        </div>
      ) : (
        ''
      )}
    </header>
  )
}

export default Header

type TMenuDropdown = {
  item: any
  data: any
  pathname: any
}

export const MenuDropdown: React.FC<TMenuDropdown> = ({ item, pathname, data }) => {
  const MenuChild =
    item.classes == 'brand'
      ? item.brand
      : data?.filter((val: any) => val?.parent_id == item?.node_id)
  return (
    <Fragment>
      <div>
        {!!MenuChild?.length && (
          <div className={`sub-menu ${!!MenuChild?.length ? '!rounded-br-none' : ''}`}>
            <div className="inner-sub">
              {MenuChild?.map((menu: any, index: number) => {
                const activeChild = pathname === menu?.content
                return (
                  <div className="relative px-[2rem] wrapper-menu " key={`${index}`}>
                    <div className="border-b border-gray10">
                      <a className={`item ${activeChild && 'active'}`}>
                        <Link href={menu?.url_key ?? ''}>
                          <div className="icons">
                            <div className="icon">
                              <a>
                                <img src={menu?.image ? menu.image : menu.logo} alt="" />
                              </a>
                            </div>
                            <div className="name ">{menu?.title}</div>
                          </div>
                        </Link>
                        {(item.classes == 'brand'
                          ? item.brand
                          : data?.filter((val: any) => val?.parent_id == menu?.node_id)
                        ).length > 0 && (
                          <div className="arrow">
                            {activeChild ? (
                              <img src="/icons/arrow-up.svg" alt="" />
                            ) : (
                              <img src="/icons/arrow-right.svg" alt="" />
                            )}
                          </div>
                        )}
                      </a>
                    </div>
                    <SubMenuItemChild pathName={item} data={data} item={menu} />
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </Fragment>
  )
}

type TSubMenu = {
  data: any
  pathName: any
  item: any
  // handleHover: React.Dispatch<boolean>;
}

export const SubMenuItemChild: React.FC<TSubMenu> = ({
  data,
  pathName,
  item,
  // handleHover,
}) => {
  const MenuChild =
    item.children !== undefined
      ? item.children
      : data.filter((val: any) => val?.parent_id == item?.node_id)
  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      exit={{ height: 0 }}
      transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
      className="wrapper-child-menu"
    >
      {MenuChild?.length > 0 ? (
        <>
          <div className="inner-sub menu-child overflow-y-scroll max-h-[70vh] no-scrollbar">
            {MenuChild?.map((menu: any, idx: number) => {
              const activeChild = pathName === menu?.content
              return (
                <div className="relative px-[1rem] wrapper-menu" key={`${idx}`}>
                  <Link href={menu?.url_key} key={menu?.content}>
                    <a
                      className={`item !py-4 ${
                        activeChild && 'active'
                      } flex items-center justify-between`}
                      // onClick={() => handleHover(false)}
                    >
                      <div className="icons flex items-center gap-2">
                        {menu?.image
                          ? menu.image
                          : menu.logo && (
                              <div className="icon">
                                <img src={menu?.image} alt="" width={24} />
                              </div>
                            )}

                        <div className="name !text-[14px] !font-normal">{menu?.title}</div>
                      </div>
                      {data.filter((val: any) => val?.parent_id == menu?.node_id).length > 0 && (
                        <div className="arrow">
                          {activeChild ? (
                            <img src="/icons/arrow-up.svg" alt="" />
                          ) : (
                            <img src="/icons/arrow-right.svg" alt="" />
                          )}
                        </div>
                      )}
                    </a>
                  </Link>
                  <SubMenuItemChild pathName={item} data={data} item={menu} />
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <></>
      )}
    </motion.div>
  )
}
